/*
 * Mocked shot data for the basic report's locked previews.
 *
 * A basic report has no shot tracking, so the cards that depend on it can't
 * render at all — which leaves the viewer with no idea what they're missing.
 * These builders synthesize a plausible advanced payload so those cards can be
 * drawn behind a blur as a preview of what upgrading yields.
 *
 * THIS IS MOCKED DATA and must never reach an unobscured surface. Every
 * consumer wraps it in `LockedPreview`, which blurs it, blocks pointer events
 * and hides it from assistive tech. Numbers are generated from a handful of
 * counts rather than copied out of a fixture: it keeps the bundle small, keeps
 * the shapes honest (the same builders the real cards use derive the rates),
 * and makes it obvious in review that nothing here was measured.
 *
 * The one thing borrowed from the real payload is identity — teams, roles and
 * crops — so the preview shows the viewer's own court rather than strangers.
 */
import type {
  PlayerStatistics,
  SegmentStatistics,
  ShotOutcomeEntry,
} from '@/types/statistics';
import type { PlayerSeries } from './contributionMetrics';
import type { LobAnalytics, ShotSpeedRow } from './shotAnalytics';
import type { StyleDatum } from './StyleOfPlay';
import type { RadarDatum } from './ShotRadar';
import { SHOT_TYPE_CATALOG } from './shotTypes';

/**
 * A rally-shot mix per player: counts by stroke, plus how many were winners.
 *
 * Errors aren't listed because they aren't a choice: the generator makes
 * exactly half of every player's shots errors, split evenly between net and
 * wall. The figures are fogged but the BARS are not, and a bar is a claim — a
 * 94% in-play sample would advertise a performance nobody measured. Dead-even
 * splits read as what they are: placeholders holding the shape.
 */
interface SampleProfile {
  /** Accepted rally shots per backend stroke key. */
  strokes: Record<string, number>;
  winners: number;
}

// Four distinguishable profiles so a preview of a 4-player card doesn't show
// the same bar four times. Ordered to match a roster's natural reading order.
// Each total is divisible by 4, which is what makes the dealing below come out
// at exactly 50/50 in-play-vs-error and exactly 50/50 net-vs-wall.
const PROFILES: SampleProfile[] = [
  {
    strokes: {
      forehand: 34,
      backhand: 29,
      forehand_volley: 21,
      backhand_volley: 16,
      overhead: 14,
      smash: 7,
      forehand_lob: 11,
      backhand_lob: 8,
    },
    winners: 7,
  },
  {
    strokes: {
      forehand: 31,
      backhand: 33,
      forehand_volley: 17,
      backhand_volley: 19,
      overhead: 11,
      smash: 4,
      forehand_lob: 14,
      backhand_lob: 11,
    },
    winners: 5,
  },
  {
    strokes: {
      forehand: 28,
      backhand: 26,
      forehand_volley: 24,
      backhand_volley: 18,
      overhead: 16,
      smash: 9,
      forehand_lob: 8,
      backhand_lob: 7,
    },
    winners: 9,
  },
  {
    strokes: {
      forehand: 36,
      backhand: 30,
      forehand_volley: 15,
      backhand_volley: 13,
      overhead: 9,
      smash: 3,
      forehand_lob: 16,
      backhand_lob: 14,
    },
    winners: 4,
  },
];

/** A structurally complete shot record. Positions are court-plausible. */
function shot(
  index: number,
  outcome: 'net' | 'wall_bounce' | 'floor_bounce' | 'player_hit',
  isWinner: boolean,
): ShotOutcomeEntry {
  // Spread the landings across the far half so a preview court looks populated
  // rather than stacked on one point.
  const x = ((index * 37) % 90) / 10 - 4.5;
  const y = 1 + ((index * 53) % 80) / 10;
  const pos = { x, y };
  return {
    rally_id: `sample-${index % 17}`,
    shot_frame: index * 61,
    player_position: { x: -x / 2, y: -y / 2 },
    player_position_mirroed: { x: x / 2, y: y / 2 },
    shot_position: pos,
    shot_position_mirroed: pos,
    outcome: { type: outcome, position: pos, position_mirroed: pos },
    accepted: true,
    is_winner: isWinner,
    is_error: outcome === 'net' || outcome === 'wall_bounce',
  } as ShotOutcomeEntry;
}

/**
 * Build one player's synthetic `shots_tracking`.
 *
 * Outcomes are dealt by position so the two bars that stay legible come out
 * dead even, whatever stroke counts a profile carries:
 *
 *   every 2nd shot is an error  → in-play 50% / error 50%
 *   alternating net and wall    → net 50% / wall 50% in the loss gauge
 *
 * The cards derive those rates themselves, through the same functions the real
 * data goes through — nothing here is a hard-coded percentage.
 */
function sampleShotsTracking(
  profile: SampleProfile,
): NonNullable<PlayerStatistics['shots_tracking']> {
  const shot_outcomes: Record<string, ShotOutcomeEntry[]> = {};
  let n = 0;
  let winnersLeft = profile.winners;

  for (const [stroke, count] of Object.entries(profile.strokes)) {
    const bucket: ShotOutcomeEntry[] = [];
    for (let i = 0; i < count; i++) {
      n++;
      if (n % 2 === 0) {
        // Half of all shots, alternating the two error kinds evenly.
        bucket.push(shot(n, n % 4 === 0 ? 'wall_bounce' : 'net', false));
      } else if (winnersLeft > 0 && n % 7 === 0) {
        bucket.push(shot(n, 'floor_bounce', true));
        winnersLeft--;
      } else {
        bucket.push(
          shot(n, n % 3 === 0 ? 'player_hit' : 'floor_bounce', false),
        );
      }
    }
    shot_outcomes[stroke] = bucket;
  }

  return {
    shot_outcomes,
    shot_distribution: {},
    // Left empty on purpose: every rate the cards show is now counted from
    // shot_outcomes, so a published block here could only ever disagree.
    performance: {},
  } as NonNullable<PlayerStatistics['shots_tracking']>;
}

/**
 * A copy of the live segment with synthetic shot tracking grafted onto every
 * player. Teams, roles, crops and match summary are the real ones, so the
 * preview is recognisably the viewer's own match.
 */
export function sampleSegment(segment: SegmentStatistics): SegmentStatistics {
  const stats = segment.statistics;
  const players_statistics: Record<string, PlayerStatistics> = {};
  Object.keys(stats.players_statistics).forEach((pid, i) => {
    players_statistics[pid] = {
      ...stats.players_statistics[pid],
      shots_tracking: sampleShotsTracking(PROFILES[i % PROFILES.length]),
    };
  });
  return {
    ...segment,
    statistics: { ...stats, players_statistics },
  };
}

/**
 * A Matchlytics Score for the locked podium (it's shown without the lines):
 * just where everyone finished, all different so the podium has its shape and
 * its crown. The crown lands on the other team's first player (the report
 * seats the viewer's team first), so the sample never flatters the viewer.
 */
export function sampleMatchlyticsScore(
  segment: SegmentStatistics,
  playerIds: string[],
): PlayerSeries {
  const ms = segment.statistics.match_summary;
  const duration = ms.end_time - ms.start_time;
  const finals = [18, 30, 42, 10];
  return {
    series: Object.fromEntries(
      playerIds.map((id, i) => [
        id,
        [
          { t: 0, value: 0 },
          { t: duration, value: finals[i % finals.length] },
        ],
      ]),
    ),
    duration,
  };
}

/**
 * A real block, for the locked Contribution card's consistency bars and error
 * rate / winner rate / winners per error charts: the first block of an
 * advanced session (b2c2675b), reduced to each
 * player's accepted rally shots as `<seconds since the block started><i|e|w>`
 * (in play, error, winner). Running rates only read true with a real game's
 * rhythm, which generated shots don't have; nothing else of that session is
 * kept.
 */
const SAMPLE_RACE_BLOCK = {
  duration: 1735.9,
  shots: {
    player1:
      '31.1i 33.4i 57.3i 60.4i 63.5w 74.0i 80.1i 92.6i 94.6i 98.4i 101.3i 144.1i 150.3i 155.2i 157.1i 180.5i 222.0i 224.4i 267.2i 269.8i 279.8e 301.6i 304.0i 308.1i 314.5w 333.8i 345.9i 362.5i 365.6i 368.3i 375.1i 404.2e 445.8e 471.0i 474.5i 493.2i 530.7i 534.6i 538.3w 566.2i 573.5i 602.1i 649.5i 654.3i 675.0i 681.9i 685.6i 708.9i 721.5i 723.5i 726.3e 834.0i 871.5i 873.8i 877.5i 880.3w 919.7i 927.8i 961.3i 963.9i 987.3i 991.5i 1010.8i 1029.1i 1031.7i 1033.3i 1038.6i 1097.2e 1110.0w 1142.2i 1146.6i 1168.8i 1171.5i 1174.6i 1205.1i 1233.8i 1236.8w 1249.3i 1269.9i 1385.7i 1404.3i 1407.6i 1411.3w 1457.0i 1459.5i 1462.2i 1488.6i 1490.9i 1493.8i 1512.0i 1514.5i 1533.9i 1539.2w 1563.3w 1576.1i 1578.8i 1592.1e 1603.4i 1606.0w',
    player2:
      '15.3i 17.6i 20.6i 46.6i 71.8i 103.6i 125.1i 152.9i 176.2i 182.7i 199.9i 258.6e 293.4e 305.6i 311.6i 331.8i 370.3i 373.5i 378.4i 418.9i 421.1i 425.5w 467.9i 495.8i 498.4i 501.9i 536.4i 569.6i 589.9i 625.7e 652.4i 669.7i 673.5i 677.3i 679.0i 710.7i 716.0i 718.5i 743.0i 757.0i 760.1i 761.8w 776.7w 811.8e 831.2i 875.9i 901.8i 959.4i 966.2i 1013.7i 1016.4w 1040.4w 1068.4i 1072.3i 1125.2i 1127.4i 1131.0i 1145.0i 1151.7i 1188.7i 1192.0i 1207.4i 1221.8i 1246.8i 1329.2i 1347.3i 1350.2w 1368.1i 1382.7i 1509.3i 1601.1i 1629.1i 1646.4w',
    player3:
      '31.9i 35.3w 59.3i 75.5i 79.4i 81.7e 93.5i 96.5i 99.3i 103.0i 105.3w 126.6i 151.5i 156.4i 181.3i 199.2i 221.2i 223.1i 225.5i 237.6e 268.5i 271.6i 302.7i 332.7i 335.7e 347.1w 363.5i 366.5i 371.1i 374.0i 472.6i 475.3w 532.7i 569.0i 590.8e 651.5i 655.8w 674.1i 683.2i 687.3w 709.8i 719.8i 722.6i 724.3i 743.9i 755.9i 756.2i 810.9i 834.7e 873.0i 875.1i 920.5e 931.5i 960.3i 962.6i 964.5i 989.2i 992.2e 1027.4i 1031.0i 1032.4i 1070.4i 1073.1w 1088.6i 1095.6i 1145.6i 1173.1i 1208.6w 1248.0i 1332.7i 1346.5i 1349.2i 1381.8i 1409.4i 1458.9i 1489.9i 1492.1i 1510.1i 1513.6i 1515.3e 1537.3i 1574.3i 1577.0i 1579.9w 1600.4i 1604.6i 1645.5i',
    player4:
      '16.8i 19.0i 21.9i 61.0i 72.6i 124.4i 145.4i 153.7i 158.4w 175.4i 178.5i 202.5i 218.7i 304.7i 306.5i 309.7i 312.9i 361.6i 369.1i 376.7i 380.2w 416.0i 419.9i 424.1i 468.9i 494.0i 496.6i 501.2i 571.5i 603.0w 653.2i 672.8i 675.9i 678.2i 713.8i 717.6i 742.2i 758.5i 775.9i 830.5i 832.3i 878.1i 1008.8i 1015.0i 1037.1i 1039.5i 1065.1i 1108.9i 1126.0i 1143.1i 1147.6i 1169.7i 1206.1i 1222.8e 1250.3w 1270.7w 1327.3i 1368.7i 1383.9i 1386.8e 1402.7i 1460.6i 1463.9e 1591.3i 1602.8i 1627.4i 1631.1i',
  },
};

/**
 * SAMPLE_RACE_BLOCK as a block (on a 10 fps clock from 0), for the locked
 * Contribution card's consistency bars and charts. Its players are the slots
 * every block uses (player1..player4).
 */
export const SAMPLE_RACE_SEGMENT = {
  statistics: {
    match_summary: {
      start_time: 0,
      end_time: SAMPLE_RACE_BLOCK.duration,
      start_frame: 0,
      end_frame: SAMPLE_RACE_BLOCK.duration * 10,
    },
    players_statistics: Object.fromEntries(
      Object.entries(SAMPLE_RACE_BLOCK.shots).map(([pid, shots]) => [
        pid,
        {
          shots_tracking: {
            shot_outcomes: {
              forehand: shots.split(' ').map((shot) => {
                const kind = shot.slice(-1);
                return {
                  shot_frame: parseFloat(shot) * 10,
                  accepted: true,
                  is_error: kind === 'e',
                  is_winner: kind === 'w',
                  outcome: { type: kind === 'e' ? 'net' : 'floor_bounce' },
                };
              }),
            },
          },
        },
      ]),
    ),
  },
} as unknown as SegmentStatistics;

/** Offensive/defensive split for the Estilo de Jogo bar. */
export function sampleStyle(name: string, color: string): StyleDatum[] {
  return [{ id: 'sample', name, color, offensive: 0.46, defensive: 0.54 }];
}

/**
 * Eight-axis shot mix, every axis on the same value, so the radar draws a
 * regular octagon.
 *
 * Same reasoning as the 50/50 outcome splits: the percentages are fogged but
 * the SHAPE isn't, and a lopsided blob would be claiming a playing style
 * nobody measured. A perfect octagon claims nothing and still shows exactly
 * what the chart does.
 *
 * Half the radius rather than a true 1/8 share: the axis value drives the
 * vertex distance directly, so 0.125 collapses the octagon into the middle
 * of the ring where it reads as broken rather than neutral.
 */
export function sampleRadar(name: string, color: string): RadarDatum[] {
  const values = SHOT_TYPE_CATALOG.map(() => 0.5);
  return [{ id: 'sample', name, color, values }];
}

/** Per-stroke average speeds, fastest first (the card sorts, this just feeds). */
export function sampleSpeedRows(): ShotSpeedRow[] {
  const speeds: Record<string, number> = {
    smash: 87.4,
    overhead: 61.2,
    forehand_volley: 54.8,
    forehand: 52.1,
    backhand_volley: 48.6,
    backhand: 45.3,
    forehand_lob: 38.7,
    backhand_lob: 36.2,
  };
  return SHOT_TYPE_CATALOG.map((axis) => ({
    type: axis.key,
    value: speeds[axis.key] ?? null,
    measurements: 12,
    state: 'ok' as ShotSpeedRow['state'],
  }));
}

/** Lob quality figures for the locked Análise de Lobs preview. */
export const SAMPLE_LOBS: LobAnalytics = {
  hasData: true,
  availability: 'complete',
  averageHeightM: 5.8,
  averageDepthM: 2.4,
  behindServiceLinePct: 0.63,
  inFrontServiceLinePct: 0.37,
  successPct: 0.58,
  serviceLine: { state: 'compatible', depthM: 3 },
  successThreshold: { state: 'compatible', depthM: 2 },
};
